import { z } from 'zod';

/** UI + email languages. `zh` is Simplified Chinese. ARCHITECTURE §5. */
export const localeSchema = z.enum(['sv', 'en', 'zh']);
export type Locale = z.infer<typeof localeSchema>;

export const DEFAULT_LOCALE: Locale = 'sv';

export const uuidSchema = z.uuid();

/** Quantities are numeric(14,3) in the article base unit — transported as strings
 *  (never JS floats) so 3-decimal precision survives the wire. */
export const qtySchema = z
  .union([z.string(), z.number()])
  .transform((v) => (typeof v === 'number' ? v.toFixed(3) : v))
  .pipe(z.string().regex(/^-?\d{1,11}(\.\d{1,3})?$/, 'invalid quantity'));

export const sortDirectionSchema = z.enum(['asc', 'desc']);
export type SortDirection = z.infer<typeof sortDirectionSchema>;

/** A single structured filter value. Arrays mean "IN (...)"; null means "IS NULL". */
export const filterValueSchema = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.null(),
  z.array(z.union([z.string(), z.number()])),
]);
export type FilterValue = z.infer<typeof filterValueSchema>;

/**
 * The one list-query contract every list endpoint accepts (ARCHITECTURE §4).
 * Server side is implemented once in `apps/api/src/core/db/list-query.ts`.
 *
 * - `q`       free-text, ILIKE across the endpoint's configured columns
 * - `filters` structured, endpoint-declared filter keys
 * - `sort`    field name, optionally prefixed `-` for descending (`-createdAt`)
 * - `page`/`pageSize` offset pagination; `cursor` keyset pagination (mutually exclusive)
 */
export const listQueryParamsSchema = z.object({
  q: z.string().trim().min(1).max(200).optional(),
  filters: z.record(z.string(), filterValueSchema).optional(),
  sort: z.string().min(1).max(80).optional(),
  dir: sortDirectionSchema.optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(500).default(50),
  cursor: z.string().min(1).max(500).optional(),
});
export type ListQueryParams = z.input<typeof listQueryParamsSchema>;
export type ResolvedListQueryParams = z.output<typeof listQueryParamsSchema>;

/** Shape of every paginated list response. */
export function listResponseSchema<T extends z.ZodTypeAny>(item: T) {
  return z.object({
    items: z.array(item),
    total: z.number().int().nonnegative().nullable(),
    page: z.number().int().min(1),
    pageSize: z.number().int().min(1),
    nextCursor: z.string().nullable(),
  });
}

export interface ListResponse<T> {
  items: T[];
  /** null when the endpoint runs in keyset mode (no cheap exact count). */
  total: number | null;
  page: number;
  pageSize: number;
  nextCursor: string | null;
}

/** Delta-sync query for the Android reference cache (ARCHITECTURE §6). */
export const deltaQuerySchema = z.object({
  since: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(1000).default(500),
});
export type DeltaQuery = z.infer<typeof deltaQuerySchema>;

export interface DeltaResponse<T> {
  items: T[];
  /** Opaque `<iso-updated_at>|<uuid>` cursor to pass back as `since`. */
  cursor: string | null;
  hasMore: boolean;
}

/** Uniform API error body produced by the global AppError filter. */
export const apiErrorSchema = z.object({
  code: z.string(),
  params: z.record(z.string(), z.unknown()).optional(),
});
export type ApiError = z.infer<typeof apiErrorSchema>;
